import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type UsersBulkResponse } from './types';
import { zodUsersBulkPayloadSchema } from './validations';

/**
 * Fetches multiple users in bulk from the API.
 * @param {UUID[]} userIds - Array of user IDs to fetch.
 * @param {boolean} includeCompanyInformation - Whether to include company information.
 * @returns {Promise<UserBulkAdmin[]>} Array of user data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-User/operation/query_users_in_bulk_vadmin_user_query_bulk_post
 */
export const fetchUsersBulkAdmin = async (
  userIds: UUID[],
  includeCompanyInformation: boolean,
  options?: KyRequestOptions,
) => {
  if (!userIds?.length) {
    return [];
  }

  try {
    const response = await vestigasApi
      .post(ENDPOINT.USER.GET_BULK_ADMIN(), {
        json: {
          ids: userIds,
          includeCompanyInformation,
        },
        validate: { body: zodUsersBulkPayloadSchema },
        ...options,
      })
      .json<UsersBulkResponse>();

    return response?.items ?? [];
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Users Bulk Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.user.getBulk.error422, {
            ns: 'data',
          });
          break;
        }

        case 429: {
          errorMessage = i18n.t(($) => $.user.getBulk.error429, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.user.getBulk.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
